import React from "react";
import { Drawer, Card, Typography, Button } from "antd";
import { PrinterOutlined, CloseCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface ReparationCase {
  idReparationCase: number;
  firstName: string;
  lastName: string;
  primaryPhone: string;
  brand: {
    brandName: string;
  };
  model: string;
}

interface PrintReparationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  reparationCase: ReparationCase | null;
}

const PrintReparationDrawer: React.FC<PrintReparationDrawerProps> = ({
  isOpen,
  onClose,
  reparationCase,
}) => {
  const printRef = React.useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (printRef.current) {
      const printContents = printRef.current.innerHTML;
      const printWindow = window.open("", "_blank", "width=600,height=600");
      if (printWindow) {
        const printHtml = `
          <html>
            <head>
              <title>Print Reparation Case</title>
              <style>
                @page {
                  size: 58mm 39mm;
                  margin: 0;
                }
                @media print {
                  html, body {
                    margin: 0;
                    padding: 0;
                    width: 100%;
                    height: 100%;
                  }
                  .printable-item {
                    width: 58mm !important;
                    height: 39mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    box-sizing: border-box;
                    overflow: hidden;
                    page-break-after: always;
                    page-break-inside: avoid;
                  }
                  .ant-card {
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    box-shadow: none !important;
                  }
                  .qr-code {
                    margin: 8px auto !important;
                    display: block !important;
                  }
                }
              </style>
            </head>
            <body>
              ${printContents}
            </body>
          </html>
        `;
        printWindow.document.open();
        (printWindow.document.write as (s: string) => void)(printHtml);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  };

  const caseId = "REP" + reparationCase?.idReparationCase.toString().padStart(5, "0");

  if (!reparationCase) {
    return null;
  }

  return (
    <Drawer
      title="Print Case Details"
      placement="bottom"
      height="100%"
      onClose={onClose}
      open={isOpen}
    >
      <div
        style={{
          position: "fixed",
          bottom: "15px",
          left: "40%",
          background: "white",
          zIndex: 5,
          padding: "10px 20px",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
          borderRadius: "10px",
        }}
      >
        <Button
          icon={<CloseCircleOutlined />}
          style={{ margin: "0 10px" }}
          onClick={onClose}
        />
        <Button
          icon={<PrinterOutlined />}
          style={{ margin: "0 10px" }}
          onClick={handlePrint}
        />
      </div>

      <div 
        ref={printRef}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Card
          style={{
            width: "100%",
            maxWidth: "58mm",
            margin: "5px auto",
          }}
          className="printable-item"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "8px",
            }}
          >
            <Text strong style={{ fontSize: "16px", marginBottom: "8px" }}>
              {caseId}
            </Text>

            <Text style={{ fontSize: "14px", marginBottom: "4px", textAlign: "center" }}>
              {reparationCase.firstName} {reparationCase.lastName}
            </Text>

            <Text style={{ fontSize: "14px", marginBottom: "4px", opacity: 0.8 }}>
              {reparationCase.primaryPhone}
            </Text>

            <Text style={{ fontSize: "12px", opacity: 0.7, marginBottom: "4px", textAlign: "center" }}>
              {reparationCase.brand.brandName} {reparationCase.model}
            </Text>
          </div>
        </Card>
      </div>
    </Drawer>
  );
};

export default PrintReparationDrawer;
